/**
 * QueuedMessage / QueuedMessagesBlock
 *
 * Collapsible "Up next" section above chat input. Each row: circle, message text,
 * edit (pencil), send now (up arrow), cancel (trash).
 */

import React, { useState } from 'react'
import {
  IconChevronDown,
  IconChevronRight,
  IconPencil,
  IconArrowUp,
  IconTrash
} from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

export interface QueuedMessageItem {
  id: string
  message: string
}

const TRUNCATE_LEN = 120

interface QueuedMessagesBlockProps {
  turns: QueuedMessageItem[]
  onRemove: (id: string) => void
  onEdit: (id: string, message: string) => void
  onSendNow: (id: string) => void
}

export const QueuedMessage: React.FC<{
  item: QueuedMessageItem
  onRemove: (id: string) => void
}> = ({ item, onRemove }) => {
  const truncated =
    item.message?.length > TRUNCATE_LEN
      ? item.message.slice(0, TRUNCATE_LEN) + '...'
      : item.message || ''
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98, y: -5 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: -5 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25, mass: 0.6 }}
      className="mb-2 flex max-w-full items-center gap-2 overflow-hidden rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-sm shadow-sm dark:border-amber-900/40 dark:bg-amber-950/30"
    >
      <span className="text-amber-600 dark:text-amber-400" aria-hidden>
        ○
      </span>
      <span className="line-clamp-1 flex-1 truncate text-left text-xs font-medium text-amber-900/90 dark:text-amber-100/90">
        {truncated}
      </span>
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="shrink-0 rounded p-1.5 text-amber-700 transition-colors hover:bg-amber-200/60 dark:text-amber-300 dark:hover:bg-amber-800/50"
        aria-label="Remove from queue"
      >
        <IconTrash size={14} />
      </button>
    </motion.div>
  )
}

export const QueuedMessagesBlock: React.FC<QueuedMessagesBlockProps> = ({
  turns,
  onRemove,
  onEdit,
  onSendNow
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const n = turns.length

  if (n === 0) return null

  return (
    <div className="mb-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-100/90 shadow-sm dark:border-neutral-700 dark:bg-neutral-800/90">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-700 dark:text-slate-200"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <IconChevronRight size={16} className="shrink-0" />
        ) : (
          <IconChevronDown size={16} className="shrink-0" />
        )}
        <span>{n === 1 ? 'Up next' : `${n} Up next`}</span>
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-200/80 px-2 py-1.5 dark:border-neutral-700/80">
              {turns.map((turn) => {
                const truncated =
                  turn.message?.length > TRUNCATE_LEN
                    ? turn.message.slice(0, TRUNCATE_LEN) + '...'
                    : turn.message || ''
                return (
                  <div
                    key={turn.id}
                    className="flex items-center gap-2 rounded py-1.5 pl-1 pr-1 hover:bg-slate-200/50 dark:hover:bg-neutral-700/50"
                  >
                    <span
                      className="text-muted-foreground shrink-0 text-[10px]"
                      aria-hidden
                    >
                      ○
                    </span>
                    <span className="line-clamp-1 min-w-0 flex-1 truncate text-left text-xs font-medium text-foreground/90">
                      {truncated}
                    </span>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onEdit(turn.id, turn.message)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-slate-300/60 hover:text-foreground dark:hover:bg-neutral-600/60"
                        aria-label="Edit queued message"
                      >
                        <IconPencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onSendNow(turn.id)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-slate-300/60 hover:text-foreground dark:hover:bg-neutral-600/60"
                        aria-label="Send now"
                      >
                        <IconArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemove(turn.id)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400"
                        aria-label="Cancel / remove from queue"
                      >
                        <IconTrash size={14} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
